import { i18n } from '@/shared/i18n'

/**
 * All backend money is integer: USD in cents, tokens in their own smallest
 * units. Formatting happens here and nowhere else, so a screen never divides by
 * a magic constant.
 */

export function formatCents(cents: number): string {
  return new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100)
}

/**
 * A token amount in its smallest units, as an integer string.
 *
 * The argument stays a string all the way through and is never turned into a
 * number. USDT on BSC has 18 decimals, so 20 USDT is 2e19 — past
 * `Number.MAX_SAFE_INTEGER`, where JavaScript starts rounding silently. That
 * amount is how the payment module recognises an invoice: a rounded one belongs
 * to no invoice at all, and the transfer becomes an orphan nobody can trace.
 *
 * So the decimal point is inserted by cutting the string, and the tail keeps
 * every significant digit — the last of them are exactly what tells one
 * invoice from another.
 */
export function formatTokenUnits(units: string, decimals: number, symbol: string): string {
  const digits = units.replace(/^0+(?=\d)/, '')
  const padded = digits.padStart(decimals + 1, '0')
  const whole = padded.slice(0, padded.length - decimals)
  const fraction = decimals === 0 ? '' : padded.slice(padded.length - decimals)

  // No locale formatting: this number is copied into a wallet, not read as a
  // quantity. A thousands separator or a comma for the point would be
  // transcribed along with it.
  const trimmed = fraction.replace(/0+$/, '').padEnd(2, '0')

  return trimmed === '' ? `${whole} ${symbol}` : `${whole}.${trimmed} ${symbol}`
}
